import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Stack,
  Typography,
  Card,
  Grid,
  Button,
  Box,
  CircularProgress,
  Switch,
  TextField,
  InputAdornment,
  Paper,
  FormControlLabel,
  Chip,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Wifi,
  Phone,
  Tv,
  Zap,
  GraduationCap,
  ChevronRight,
  Plus,
  Layers,
  Sliders,
  Search,
  CheckCircle2,
  AlertTriangle,
  Edit3,
  RefreshCw,
  Power,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Page from '../components/Page';
import ModalC from './components/CModal';
import CategoryControl from './components/CategoryControl';
import {
  getServices,
  getServiceProviders,
  updateServiceImages,
  convertServicesBatchToV2,
  getServicesAvailability,
  getCategoriesAvailability,
  changeServicesAvailability,
  changeCategoryAvailability,
  reorderCategoryAvailability,
} from '../api/services';
import { BRAND_IMAGES } from '../utils/assets';

// Service Categories Meta matching screenshot
const CATEGORIES_META = [
  {
    key: 'Data',
    title: 'Data bundles',
    icon: Wifi,
    color: '#0066FE',
    bg: '#EFF6FF',
    description: 'SME, Corporate and Direct Data across all networks',
  },
  {
    key: 'Airtime',
    title: 'Airtime VTU and share',
    icon: Phone,
    color: '#16A34A',
    bg: '#DCFCE7',
    description: 'Top-up and share airtime per network',
  },
  {
    key: 'Cable',
    title: 'Cable TV',
    icon: Tv,
    color: '#D97706',
    bg: '#FEF3C7',
    description: 'DSTV, GOTV, Startimes and ShowMax',
  },
  {
    key: 'Electricity',
    title: 'Electricity bills',
    icon: Zap,
    color: '#EA580C',
    bg: '#FFEDD5',
    description: 'Prepaid and postpaid tokens across DISCOs',
  },
  {
    key: 'ResultCheck',
    title: 'Result checkers',
    icon: GraduationCap,
    color: '#7C3AED',
    bg: '#EDE9FE',
    description: 'WAEC, NECO and NABTEB e-PINs',
  },
];

// SVG Circular Progress Ring matching the screenshot
function CircularMetric({ percentage }) {
  const size = 96;
  const strokeWidth = 9;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#F1F3F5"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#16A34A"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="none"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <Typography
        sx={{
          position: 'absolute',
          fontWeight: 800,
          fontSize: '1.25rem',
          color: '#111827',
          letterSpacing: '-0.02em',
        }}
      >
        {percentage}%
      </Typography>
    </Box>
  );
}

CircularMetric.propTypes = {
  percentage: PropTypes.number.isRequired,
};

export default function ServicesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'catalog';

  // Catalog State
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [categoryFilterTab, setCategoryFilterTab] = useState('all');
  const [updatingImages, setUpdatingImages] = useState(false);
  const [convertingAll, setConvertingAll] = useState(false);

  // Availability & Controls State
  const [masterServices, setMasterServices] = useState([]);
  const [categoriesAvailability, setCategoriesAvailability] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [categoryServiceFilter, setCategoryServiceFilter] = useState('All');
  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  // Master Service Modal State
  const [selectedMasterService, setSelectedMasterService] = useState(null);
  const [masterModalStatus, setMasterModalStatus] = useState(true);
  const [masterModalMessage, setMasterModalMessage] = useState('');
  const [savingMasterService, setSavingMasterService] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch Catalog Data
  const fetchCatalogData = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [srvRes, provRes] = await Promise.all([getServices(), getServiceProviders()]);
      if (srvRes.ok && srvRes.data?.data) {
        setServices(srvRes.data.data);
      }
      if (provRes.ok && provRes.data?.data) {
        setProviders(provRes.data.data);
      }
    } catch (err) {
      toast.error('Failed to load services data');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  // Fetch Availability Data
  const fetchAvailabilityData = useCallback(async () => {
    setAvailabilityLoading(true);
    try {
      const [srvRes, catRes] = await Promise.all([
        getServicesAvailability(),
        getCategoriesAvailability(),
      ]);
      if (srvRes.ok && srvRes.data?.data) {
        setMasterServices(srvRes.data.data);
      }
      if (catRes.ok && catRes.data?.data) {
        setCategoriesAvailability(catRes.data.data);
      }
    } catch (err) {
      toast.error('Failed to load availability controls data');
    } finally {
      setAvailabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalogData();
    fetchAvailabilityData();
  }, [fetchCatalogData, fetchAvailabilityData]);

  // Compute product count map
  const productCountMap = useMemo(() => {
    const map = {};
    services.forEach((s) => {
      if (s.category) {
        const key = s.category.trim().toLowerCase();
        map[key] = (map[key] || 0) + 1;
      }
    });
    return map;
  }, [services]);

  // Master services enriched with meta
  const enrichedMasterServices = useMemo(
    () =>
      CATEGORIES_META.map((meta) => {
        const found = masterServices.find(
          (s) => (s.name || '').toLowerCase() === meta.key.toLowerCase()
        );
        return {
          id: found?.id ?? meta.key,
          name: meta.key,
          title: meta.title,
          icon: meta.icon,
          color: meta.color,
          bg: meta.bg,
          status: found ? Boolean(found.status) : true,
          message: found?.message || 'Service unavailable at the moment',
          hasRecord: Boolean(found),
        };
      }),
    [masterServices]
  );

  // Categories sorted by index
  const sortedCategories = useMemo(
    () => [...categoriesAvailability].sort((a, b) => (a.index ?? 0) - (b.index ?? 0)),
    [categoriesAvailability]
  );

  // Categories filtered by service tab
  const tabFilteredCategories = useMemo(() => {
    if (categoryServiceFilter === 'All') return sortedCategories;
    return sortedCategories.filter(
      (c) => (c.service || '').toLowerCase() === categoryServiceFilter.toLowerCase()
    );
  }, [sortedCategories, categoryServiceFilter]);

  // Categories filtered by search query
  const visibleCategories = useMemo(() => {
    if (!categorySearchQuery.trim()) return tabFilteredCategories;
    const q = categorySearchQuery.trim().toLowerCase();
    return tabFilteredCategories.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.service || '').toLowerCase().includes(q)
    );
  }, [tabFilteredCategories, categorySearchQuery]);

  // Counts for category tabs
  const categoryTabCounts = useMemo(() => {
    const counts = { All: sortedCategories.length };
    CATEGORIES_META.forEach((cat) => {
      counts[cat.key] = sortedCategories.filter(
        (c) => (c.service || '').toLowerCase() === cat.key.toLowerCase()
      ).length;
    });
    return counts;
  }, [sortedCategories]);

  // Compute stats for catalog overview
  const totalServicesCount = services.length;
  const v2ActiveCount = useMemo(() => services.filter((s) => s.useV2Engine).length, [services]);
  const legacyCount = totalServicesCount - v2ActiveCount;
  const activeProvidersCount = useMemo(() => providers.filter((p) => p.isActive).length, [providers]);
  const v2Percentage = totalServicesCount > 0 ? Math.round((v2ActiveCount / totalServicesCount) * 100) : 0;
  const activeMasterCount = enrichedMasterServices.filter((s) => s.status).length;
  const activeCategoriesCount = sortedCategories.filter((c) => c.status).length;

  // Actions
  const handleMigrateAllLegacy = async () => {
    const legacyList = services.filter((s) => !s.useV2Engine);
    if (legacyList.length === 0) return;
    setConvertingAll(true);
    try {
      const { successCount, total } = await convertServicesBatchToV2(legacyList);
      toast.success(`Successfully migrated ${successCount} of ${total} legacy services to V2!`);
      fetchCatalogData();
    } catch (err) {
      toast.error('Failed to migrate services to V2');
    } finally {
      setConvertingAll(false);
    }
  };

  const handleUpdateImages = async () => {
    setUpdatingImages(true);
    try {
      const res = await updateServiceImages(BRAND_IMAGES);
      if (res.ok) {
        toast.success('Service brand images synced successfully!');
        fetchCatalogData();
      } else {
        toast.error(res.data?.message || 'Failed to update images');
      }
    } catch (err) {
      toast.error('Error updating service images');
    } finally {
      setUpdatingImages(false);
    }
  };

  // Quick toggle master service
  const handleToggleMasterService = async (serviceObj, newStatus) => {
    if (!serviceObj.hasRecord) {
      toast.info('Initialising service in system...');
    }
    // Optimistic update
    setMasterServices((prev) =>
      prev.map((s) => (s.id === serviceObj.id ? { ...s, status: newStatus } : s))
    );

    try {
      const res = await changeServicesAvailability(serviceObj.id, {
        status: newStatus,
        message: serviceObj.message || 'Service unavailable at the moment',
      });
      if (res.ok) {
        toast.success(`${serviceObj.title || serviceObj.name} checkout ${newStatus ? 'enabled' : 'disabled'}`);
        fetchAvailabilityData();
      } else {
        toast.error(res.data?.message || 'Failed to update service status');
        fetchAvailabilityData();
      }
    } catch (err) {
      toast.error('Error updating service status');
      fetchAvailabilityData();
    }
  };

  // Open modal to configure master service
  const handleOpenMasterModal = (serviceObj) => {
    setSelectedMasterService(serviceObj);
    setMasterModalStatus(serviceObj.status);
    setMasterModalMessage(serviceObj.message);
  };

  const handleSaveMasterModal = async () => {
    if (!selectedMasterService) return;
    setSavingMasterService(true);
    try {
      const res = await changeServicesAvailability(selectedMasterService.id, {
        status: masterModalStatus,
        message: masterModalMessage,
      });
      if (res.ok) {
        toast.success(`${selectedMasterService.title || selectedMasterService.name} updated`);
        setSelectedMasterService(null);
        fetchAvailabilityData();
      } else {
        toast.error(res.data?.message || 'Failed to update service');
      }
    } catch (err) {
      toast.error('Error saving master service settings');
    } finally {
      setSavingMasterService(false);
    }
  };

  // Reorder categories inside current tab
  const handleReorderInTab = async (updatedList) => {
    const payload = updatedList.map((item, idx) => ({
      id: item.id,
      index: idx,
    }));

    // Optimistic local state update
    setCategoriesAvailability((prev) => {
      const map = new Map(updatedList.map((item, idx) => [item.id, { ...item, index: idx }]));
      return prev.map((item) => (map.has(item.id) ? map.get(item.id) : item));
    });

    try {
      const res = await reorderCategoryAvailability(payload);
      if (!res.ok) {
        toast.error('Failed to save display sort order');
        fetchAvailabilityData();
      }
    } catch (err) {
      toast.error('Error reordering categories');
      fetchAvailabilityData();
    }
  };

  // Drag End handler
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visibleCategories.findIndex((item) => item.id === active.id);
    const newIndex = visibleCategories.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(visibleCategories, oldIndex, newIndex);
    await handleReorderInTab(reordered);
  };

  // Move up/down arrow handler
  const handleMoveCategory = async (itemToMove, direction) => {
    const currentIndex = visibleCategories.findIndex((item) => item.id === itemToMove.id);
    if (currentIndex === -1) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= visibleCategories.length) return;

    const reordered = arrayMove(visibleCategories, currentIndex, targetIndex);
    await handleReorderInTab(reordered);
  };

  // 1-click toggle category checkout status
  const handleToggleCategoryStatus = async (categoryId, newStatus) => {
    setCategoriesAvailability((prev) =>
      prev.map((c) => (c.id === categoryId ? { ...c, status: newStatus } : c))
    );

    try {
      const res = await changeCategoryAvailability(categoryId, { status: newStatus });
      if (res.ok) {
        toast.success(`Category checkout ${newStatus ? 'activated' : 'disabled'}`);
      } else {
        toast.error(res.data?.message || 'Failed to update category');
        fetchAvailabilityData();
      }
    } catch (err) {
      toast.error('Error updating category status');
      fetchAvailabilityData();
    }
  };

  // Save diagnostic settings for category
  const handleChangeCategorySettings = async (categoryId, payload) => {
    setCategoriesAvailability((prev) =>
      prev.map((c) => (c.id === categoryId ? { ...c, ...payload } : c))
    );

    try {
      const res = await changeCategoryAvailability(categoryId, payload);
      if (res.ok) {
        toast.success('Category updated successfully');
      } else {
        toast.error(res.data?.message || 'Failed to update category');
        fetchAvailabilityData();
      }
    } catch (err) {
      toast.error('Error updating category');
      fetchAvailabilityData();
    }
  };

  // Filter categories according to the segmented tab (All, V2 active, Not routed)
  const filteredCategoriesMeta = useMemo(
    () =>
      CATEGORIES_META.filter((cat) => {
        const catServices = services.filter(
          (s) => (s.service || '').toLowerCase() === cat.key.toLowerCase()
        );
        const catV2Count = catServices.filter((s) => s.useV2Engine).length;

        if (categoryFilterTab === 'v2') {
          return catV2Count > 0;
        }
        if (categoryFilterTab === 'not_routed') {
          return catV2Count === 0;
        }
        return true;
      }),
    [services, categoryFilterTab]
  );

  return (
    <Page title="Services & Controls | Dashboard">
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Header Bar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          mb={3}
        >
          <div>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: '#111827',
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.5rem', sm: '1.85rem' },
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              Services & Controls
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
              Manage catalog products, routing, master kill-switches and category availability.
            </Typography>
          </div>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {currentTab === 'catalog' ? (
              <>
                <Button
                  onClick={handleUpdateImages}
                  disabled={updatingImages}
                  sx={{
                    borderRadius: '24px',
                    bgcolor: '#EAECEF',
                    color: '#1F2937',
                    px: 2.5,
                    py: 0.85,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#DFE2E6',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {updatingImages ? 'Syncing...' : 'Sync logos'}
                </Button>
                <Button
                  onClick={fetchCatalogData}
                  disabled={catalogLoading}
                  sx={{
                    borderRadius: '24px',
                    bgcolor: '#EAECEF',
                    color: '#1F2937',
                    px: 2.5,
                    py: 0.85,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#DFE2E6',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  onClick={() => navigate('/dashboard/services/create')}
                  startIcon={<Plus size={16} />}
                  sx={{
                    borderRadius: '24px',
                    bgcolor: '#0066FE',
                    color: '#FFFFFF',
                    px: 2.75,
                    py: 0.85,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#0052CC',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Add product
                </Button>
              </>
            ) : (
              <Button
                onClick={fetchAvailabilityData}
                disabled={availabilityLoading}
                startIcon={<RefreshCw size={16} />}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#EAECEF',
                  color: '#1F2937',
                  px: 2.5,
                  py: 0.85,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#DFE2E6',
                    boxShadow: 'none',
                  },
                }}
              >
                Refresh controls
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Top-Level View Tabs Switcher */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            p: '4px',
            borderRadius: '20px',
            bgcolor: '#F1F3F5',
            mb: 4,
          }}
        >
          <Button
            startIcon={<Layers size={17} />}
            onClick={() => setSearchParams({ tab: 'catalog' })}
            sx={{
              borderRadius: '16px',
              px: 2.5,
              py: 0.85,
              fontWeight: currentTab === 'catalog' ? 700 : 500,
              fontSize: '0.875rem',
              textTransform: 'none',
              bgcolor: currentTab === 'catalog' ? '#FFFFFF' : 'transparent',
              color: currentTab === 'catalog' ? '#111827' : '#6B7280',
              boxShadow: currentTab === 'catalog' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              '&:hover': {
                bgcolor: currentTab === 'catalog' ? '#FFFFFF' : 'rgba(0,0,0,0.04)',
                boxShadow: currentTab === 'catalog' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              },
            }}
          >
            Product Catalog & Routing
          </Button>

          <Button
            startIcon={<Sliders size={17} />}
            onClick={() => setSearchParams({ tab: 'availability' })}
            sx={{
              borderRadius: '16px',
              px: 2.5,
              py: 0.85,
              fontWeight: currentTab === 'availability' ? 700 : 500,
              fontSize: '0.875rem',
              textTransform: 'none',
              bgcolor: currentTab === 'availability' ? '#FFFFFF' : 'transparent',
              color: currentTab === 'availability' ? '#111827' : '#6B7280',
              boxShadow: currentTab === 'availability' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              '&:hover': {
                bgcolor: currentTab === 'availability' ? '#FFFFFF' : 'rgba(0,0,0,0.04)',
                boxShadow: currentTab === 'availability' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              },
            }}
          >
            Service & Category Controls
          </Button>
        </Box>

        {/* VIEW 1: PRODUCT CATALOG & ROUTING */}
        {currentTab === 'catalog' && (
          <>
            {/* Top Summary Card */}
            <Card
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: '24px',
                bgcolor: '#FFFFFF',
                border: '1px solid #ECEEF1',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                mb: 4,
              }}
            >
              <Grid container spacing={3} alignItems="center">
                {/* Left Col: Circular Ring + Catalog Stats */}
                <Grid item xs={12} md={8}>
                  <Stack direction="row" alignItems="center" spacing={3.5}>
                    <CircularMetric percentage={v2Percentage} />

                    <div>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                        Catalog services
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '2.25rem', sm: '2.75rem' },
                          fontWeight: 800,
                          color: '#111827',
                          lineHeight: 1.1,
                          letterSpacing: '-0.02em',
                          mb: 0.75,
                        }}
                      >
                        {totalServicesCount}
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
                        {v2ActiveCount} service{v2ActiveCount === 1 ? '' : 's'} routed through V2 multi-processor
                      </Typography>
                    </div>
                  </Stack>
                </Grid>

                {/* Right Col: Vendor Accounts & Categories */}
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    borderLeft: { md: '1px solid #ECEEF1' },
                    pl: { md: 4 },
                  }}
                >
                  <Box mb={2}>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: 500, mb: 0.25 }}>
                      Vendor accounts
                    </Typography>
                    <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, color: '#16A34A', lineHeight: 1.2 }}>
                      {activeProvidersCount} active
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: 500, mb: 0.25 }}>
                      Categories
                    </Typography>
                    <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>
                      {CATEGORIES_META.length}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Migration Callout Card when legacy services exist */}
            {legacyCount > 0 && (
              <Card
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: '20px',
                  bgcolor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 4,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '14px',
                      bgcolor: '#FEF3C7',
                      color: '#D97706',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Zap size={22} />
                  </Box>
                  <div>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#92400E' }}>
                      {legacyCount} services ready for V2 migration
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#B45309' }}>
                      Convert your legacy services to enable automated multi-processor failover routing and provider sync.
                    </Typography>
                  </div>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="contained"
                    onClick={handleMigrateAllLegacy}
                    disabled={convertingAll}
                    sx={{
                      borderRadius: '20px',
                      bgcolor: '#16A34A',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      textTransform: 'none',
                      px: 2.5,
                      py: 0.8,
                      '&:hover': { bgcolor: '#15803D' },
                    }}
                  >
                    {convertingAll ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      `⚡ Migrate All (${legacyCount}) to V2`
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard/services-legacy')}
                    sx={{
                      borderRadius: '20px',
                      borderColor: '#D97706',
                      color: '#92400E',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      textTransform: 'none',
                      px: 2,
                      py: 0.8,
                      '&:hover': { bgcolor: '#FEF3C7' },
                    }}
                  >
                    Services (Legacy)
                  </Button>
                </Stack>
              </Card>
            )}

            {/* Categories Section Header & Segmented Filter Tabs */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2.5}
              flexWrap="wrap"
              gap={1.5}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.01em',
                  fontSize: '1.35rem',
                }}
              >
                Categories
              </Typography>

              <Box
                sx={{
                  display: 'inline-flex',
                  p: '3px',
                  borderRadius: '24px',
                  bgcolor: '#F1F3F5',
                }}
              >
                {[
                  { id: 'all', label: 'All' },
                  { id: 'v2', label: 'V2 active' },
                  { id: 'not_routed', label: legacyCount > 0 ? `Legacy (${legacyCount})` : 'Legacy' },
                ].map((tab) => {
                  const isActive = categoryFilterTab === tab.id;
                  return (
                    <Button
                      key={tab.id}
                      onClick={() => setCategoryFilterTab(tab.id)}
                      sx={{
                        borderRadius: '20px',
                        px: 2,
                        py: 0.55,
                        fontSize: '0.85rem',
                        fontWeight: isActive ? 600 : 500,
                        textTransform: 'none',
                        bgcolor: isActive ? '#FFFFFF' : 'transparent',
                        color: isActive ? '#111827' : '#6B7280',
                        boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                        '&:hover': {
                          bgcolor: isActive ? '#FFFFFF' : 'rgba(0,0,0,0.04)',
                          boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                        },
                      }}
                    >
                      {tab.label}
                    </Button>
                  );
                })}
              </Box>
            </Stack>

            {catalogLoading ? (
              <Box display="flex" justifyContent="center" py={10}>
                <CircularProgress size={36} />
              </Box>
            ) : (
              <Card
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #ECEEF1',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  overflow: 'hidden',
                }}
              >
                {filteredCategoriesMeta.length === 0 ? (
                  <Box sx={{ p: 5, textAlign: 'center', color: '#6B7280' }}>
                    <Typography variant="body2">No categories match the selected filter.</Typography>
                  </Box>
                ) : (
                  filteredCategoriesMeta.map((cat, idx) => {
                    const IconComp = cat.icon;
                    const catServices = services.filter(
                      (s) => (s.service || '').toLowerCase() === cat.key.toLowerCase()
                    );
                    const count = catServices.length;
                    const v2Count = catServices.filter((s) => s.useV2Engine).length;
                    const pct = count > 0 ? Math.round((v2Count / count) * 100) : 0;
                    const isLast = idx === filteredCategoriesMeta.length - 1;

                    return (
                      <Box
                        key={cat.key}
                        onClick={() => {
                          navigate(`/dashboard/services/category/${cat.key.toLowerCase()}`);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: { xs: 2.5, sm: 3 },
                          borderBottom: isLast ? 'none' : '1px solid #F3F4F6',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          '&:hover': {
                            bgcolor: '#F9FAFB',
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2.5}>
                          <Box
                            sx={{
                              width: 52,
                              height: 52,
                              borderRadius: '16px',
                              bgcolor: cat.bg,
                              color: cat.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <IconComp size={24} />
                          </Box>

                          <div>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827', mb: 0.25 }}>
                              {cat.title}
                            </Typography>
                            <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
                              {cat.description}
                            </Typography>
                          </div>
                        </Stack>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>
                                {v2Count} on V2
                              </Typography>
                              <Typography sx={{ color: count - v2Count > 0 ? '#D97706' : '#6B7280', fontSize: '0.775rem' }}>
                                {count - v2Count > 0 ? `${count - v2Count} legacy` : `${count} total`}
                              </Typography>
                            </Box>
                            <ChevronRight size={18} color="#9CA3AF" />
                          </Box>

                          <Box
                            sx={{
                              width: { xs: 120, sm: 160 },
                              height: 4,
                              borderRadius: 2,
                              bgcolor: '#F1F3F5',
                              overflow: 'hidden',
                              mt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: `${pct}%`,
                                height: '100%',
                                bgcolor: '#16A34A',
                                borderRadius: 2,
                                transition: 'width 0.4s ease',
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Card>
            )}
          </>
        )}

        {/* VIEW 2: SERVICE & CATEGORY CONTROLS */}
        {currentTab === 'availability' && (
          <Stack spacing={4}>
            {/* SECTION 1: MASTER SERVICE KILL-SWITCHES */}
            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={1}
                mb={2.5}
              >
                <div>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: '#111827',
                      letterSpacing: '-0.01em',
                      fontSize: '1.35rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                    }}
                  >
                    <Power size={22} color="#0066FE" />
                    Master Service Kill-Switches
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.25 }}>
                    Turn off customer checkout across an entire service line instantly (e.g. during vendor downtime).
                  </Typography>
                </div>

                <Chip
                  icon={activeMasterCount === 5 ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                  label={`${activeMasterCount} of ${enrichedMasterServices.length} services active`}
                  color={activeMasterCount === 5 ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ fontWeight: 600, borderRadius: '12px' }}
                />
              </Stack>

              <Grid container spacing={2.5}>
                {enrichedMasterServices.map((serviceObj) => {
                  const IconComp = serviceObj.icon;
                  return (
                    <Grid item xs={12} sm={6} md={2.4} key={serviceObj.name}>
                      <Card
                        sx={{
                          p: 2.5,
                          borderRadius: '20px',
                          bgcolor: '#FFFFFF',
                          border: serviceObj.status ? '1px solid #ECEEF1' : '1px solid #FECACA',
                          boxShadow: serviceObj.status
                            ? '0 1px 3px rgba(0,0,0,0.02)'
                            : '0 4px 12px rgba(239, 68, 68, 0.08)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
                          },
                        }}
                      >
                        <div>
                          {/* Card Header: Icon & Switch */}
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '14px',
                                bgcolor: serviceObj.bg,
                                color: serviceObj.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <IconComp size={22} />
                            </Box>
                            <Switch
                              checked={serviceObj.status}
                              onChange={(e) => handleToggleMasterService(serviceObj, e.target.checked)}
                              color="success"
                              size="medium"
                            />
                          </Stack>

                          {/* Service Title & Status Pill */}
                          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827', mb: 0.5 }}>
                            {serviceObj.title}
                          </Typography>

                          <Box mb={1.5}>
                            <Chip
                              size="small"
                              label={serviceObj.status ? 'Checkout ACTIVE' : 'Checkout DISABLED'}
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                bgcolor: serviceObj.status ? '#DCFCE7' : '#FEE2E2',
                                color: serviceObj.status ? '#15803D' : '#B91C1C',
                                borderRadius: '6px',
                              }}
                            />
                          </Box>

                          {/* Customer Outage Notice banner if disabled */}
                          {!serviceObj.status && (
                            <Box
                              sx={{
                                bgcolor: '#FEF2F2',
                                border: '1px dashed #FCA5A5',
                                borderRadius: '10px',
                                p: 1.25,
                                mb: 1.5,
                              }}
                            >
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#991B1B' }}>
                                Notice to users:
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#7F1D1D', fontStyle: 'italic', mt: 0.25 }}>
                                &quot;{serviceObj.message}&quot;
                              </Typography>
                            </Box>
                          )}
                        </div>

                        {/* Configure Notice Button */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Edit3 size={14} />}
                          onClick={() => handleOpenMasterModal(serviceObj)}
                          sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            mt: 1,
                            borderColor: '#E2E8F0',
                            color: '#475569',
                            '&:hover': {
                              borderColor: '#CBD5E1',
                              bgcolor: '#F8FAFC',
                            },
                          }}
                        >
                          Configure Notice
                        </Button>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* SECTION 2: CATEGORY AVAILABILITY & DISPLAY SORT ORDER */}
            <Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                gap={2}
                mb={2.5}
              >
                <div>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: '#111827',
                      letterSpacing: '-0.01em',
                      fontSize: '1.35rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                    }}
                  >
                    <Sliders size={22} color="#0066FE" />
                    Category Availability & Display Sort Order
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.25 }}>
                    Drag items to reorder how categories display in your mobile and web apps, or disable checkout per category.
                  </Typography>
                </div>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip
                    label={`${activeCategoriesCount} of ${sortedCategories.length} categories active`}
                    color="default"
                    variant="outlined"
                    sx={{ fontWeight: 600, borderRadius: '12px', bgcolor: '#F8FAFC' }}
                  />
                </Stack>
              </Stack>

              {/* Filters & Search Toolbar */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: '20px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #ECEEF1',
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Service Tabs */}
                  <Grid item xs={12} lg={8}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {['All', 'Data', 'Airtime', 'Cable', 'Electricity', 'ResultCheck'].map((tabKey) => {
                        const isSelected = categoryServiceFilter === tabKey;
                        const count = categoryTabCounts[tabKey] || 0;
                        return (
                          <Button
                            key={tabKey}
                            onClick={() => setCategoryServiceFilter(tabKey)}
                            sx={{
                              borderRadius: '16px',
                              px: 2,
                              py: 0.6,
                              fontSize: '0.85rem',
                              fontWeight: isSelected ? 700 : 500,
                              textTransform: 'none',
                              bgcolor: isSelected ? '#111827' : '#F1F3F5',
                              color: isSelected ? '#FFFFFF' : '#4B5563',
                              '&:hover': {
                                bgcolor: isSelected ? '#000000' : '#E5E7EB',
                              },
                            }}
                          >
                            {tabKey === 'ResultCheck' ? 'Result Checkers' : tabKey}
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                px: 1,
                                py: 0.1,
                                fontSize: '0.72rem',
                                borderRadius: '10px',
                                bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)',
                                color: isSelected ? '#FFFFFF' : '#6B7280',
                                fontWeight: 700,
                              }}
                            >
                              {count}
                            </Box>
                          </Button>
                        );
                      })}
                    </Box>
                  </Grid>

                  {/* Search Field */}
                  <Grid item xs={12} lg={4}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search categories (e.g. SME, DSTV, Glo)..."
                      value={categorySearchQuery}
                      onChange={(e) => setCategorySearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={16} color="#9CA3AF" />
                          </InputAdornment>
                        ),
                        endAdornment: categorySearchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setCategorySearchQuery('')}>
                              <X size={14} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '14px',
                          bgcolor: '#F8FAFC',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* DnD Sortable Container */}
              {(() => {
                if (availabilityLoading) {
                  return (
                    <Box display="flex" justifyContent="center" py={8}>
                      <CircularProgress size={36} />
                    </Box>
                  );
                }

                if (visibleCategories.length === 0) {
                  return (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 6,
                        borderRadius: '20px',
                        textAlign: 'center',
                        border: '1px dashed #CBD5E1',
                        bgcolor: '#F8FAFC',
                      }}
                    >
                      <Typography variant="body1" fontWeight={600} color="#475569" mb={0.5}>
                        No categories found
                      </Typography>
                      <Typography variant="body2" color="#64748B">
                        {categorySearchQuery
                          ? `No category matching "${categorySearchQuery}" in ${categoryServiceFilter}`
                          : 'No categories available in this service filter.'}
                      </Typography>
                    </Paper>
                  );
                }

                return (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={visibleCategories.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack spacing={1.5}>
                        {visibleCategories.map((category, index) => {
                          const prodCount = productCountMap[category.name?.toLowerCase()] || 0;
                          return (
                            <CategoryControl
                              key={category.id}
                              id={category.id}
                              name={category.name}
                              service={category.service}
                              status={category.status}
                              indexValue={category.index ?? index}
                              productCount={prodCount}
                              successRate={category.successRate}
                              successRateAuto={category.successRateAuto}
                              onToggleStatus={handleToggleCategoryStatus}
                              onMoveUp={() => handleMoveCategory(category, 'up')}
                              onMoveDown={() => handleMoveCategory(category, 'down')}
                              showUpButton={index > 0}
                              showDownButton={index < visibleCategories.length - 1}
                              changeAppCategoryAvailability={handleChangeCategorySettings}
                            />
                          );
                        })}
                      </Stack>
                    </SortableContext>
                  </DndContext>
                );
              })()}
            </Box>
          </Stack>
        )}

        {/* Master Service Outage Configuration Modal */}
        <ModalC
          isOpen={Boolean(selectedMasterService)}
          setOpen={() => setSelectedMasterService(null)}
        >
          {selectedMasterService && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Configure Master Service: {selectedMasterService.title || selectedMasterService.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Disable or enable customer checkout across this entire service.
                </Typography>
              </Box>

              <Box sx={{ bgcolor: '#F8FAFC', p: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={masterModalStatus}
                      onChange={(e) => setMasterModalStatus(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="subtitle2" fontWeight={700} color={masterModalStatus ? 'success.main' : 'error.main'}>
                      {masterModalStatus ? 'Checkout is ACTIVE' : 'Checkout is DISABLED'}
                    </Typography>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {masterModalStatus
                    ? 'Users can purchase any products under this service normally.'
                    : 'All products under this service will reject orders and show the outage message below.'}
                </Typography>
              </Box>

              {!masterModalStatus && (
                <TextField
                  label="Customer Outage Notice"
                  multiline
                  rows={3}
                  value={masterModalMessage}
                  onChange={(e) => setMasterModalMessage(e.target.value)}
                  placeholder="State the reason to be shown to customers (e.g. Service temporarily down for maintenance)"
                  variant="outlined"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}

              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setSelectedMasterService(null)}
                  sx={{ borderRadius: 2 }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  loading={savingMasterService}
                  onClick={handleSaveMasterModal}
                  sx={{ borderRadius: 2, bgcolor: '#0066FE', '&:hover': { bgcolor: '#0052CC' } }}
                >
                  Save Changes
                </LoadingButton>
              </Stack>
            </Stack>
          )}
        </ModalC>
      </Container>
    </Page>
  );
}
